import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, Output, EventEmitter, Input } from '@angular/core';
import { Observable, Subject, fromEvent } from 'rxjs';
import { takeUntil, map, startWith, distinctUntilChanged } from 'rxjs/operators';
import { UIStateService } from '../services/ui-state.service';
import { ResponsiveLayoutService } from '../services/responsive-layout.service';
import { BuilderScreenBetaService } from '../services/builder-screen.service';

/**
 * NOTA: Funcionalidades temporariamente ocultas (podem ser reativadas no HTML):
 * - Desfazer/Refazer (onUndo, onRedo)
 * - Sugestões de IA (onShowAISuggestions)
 * - Predefinições (onShowPresets)
 * - Inspetor Flutuante (onToggleFloatingInspector)
 * - Mostrar Templates (onShowTemplates)
 * - Importar/Exportar JSON (onImportJson, onExportJson)
 * - Ajuda (onShowHelp)
 * 
 * Os métodos permanecem no código para facilitar reativação futura.
 */
@Component({
  selector: 'app-sticky-action-bar',
  templateUrl: './sticky-action-bar.component.html',
  styleUrls: ['./sticky-action-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone:false
})
export class StickyActionBarComponent implements OnInit, OnDestroy {
  @Input() currentTemplateId: number | null = null;
  
  @Output() showHelp = new EventEmitter<void>();
  @Output() showPresets = new EventEmitter<void>();
  @Output() showAISuggestions = new EventEmitter<void>();
  @Output() showTemplates = new EventEmitter<void>();
  @Output() openTemplateManager = new EventEmitter<void>();
  @Output() openAutoScreenGenerator = new EventEmitter<void>();
  @Output() openOptimizedAutoScreenGenerator = new EventEmitter<void>();
  @Output() openSaveTemplateModal = new EventEmitter<void>();
  @Output() openLauncherModal = new EventEmitter<void>();
  
  private destroy$ = new Subject<void>();
  
  isScrolled$: Observable<boolean> = fromEvent(window, 'scroll').pipe(
    startWith(0),
    map(() => window.scrollY > 10),
    distinctUntilChanged(),
    takeUntil(this.destroy$)
  );

  isSaving$ = this.builderService.isSaving$;
  hasUnsavedChanges$ = this.builderService.hasUnsavedChanges$;
  canUndo$ = this.builderService.canUndo$;
  canRedo$ = this.builderService.canRedo$;
  
  layoutConfig$ = this.responsiveLayout.layoutConfig$;
  screenSize$ = this.responsiveLayout.screenSize$;

  constructor(
    private uiState: UIStateService,
    private responsiveLayout: ResponsiveLayoutService,
    private builderService: BuilderScreenBetaService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Atualiza estado da UI quando rolagem muda
    this.isScrolled$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(isScrolled => {
      this.uiState.updateLayout({ stickyActionsVisible: true });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSave() {
    this.builderService.saveConfiguration();
  }

  onCancel() {
    this.builderService.cancelChanges();
  }

  onUndo() {
    this.builderService.undo();
  }

  onRedo() {
    this.builderService.redo();
  }

  onTogglePreview() {
    const currentPreviewMode = this.uiState.getCurrentState().workspace.previewMode;
    this.uiState.updateWorkspace({ previewMode: !currentPreviewMode });
  }

  onToggleGrid() {
    const currentGridVisible = this.uiState.getCurrentState().workspace.gridVisible;
    this.uiState.updateWorkspace({ gridVisible: !currentGridVisible });
  }

  onZoomIn() {
    const currentZoom = this.uiState.getCurrentState().workspace.zoom;
    const newZoom = Math.min(currentZoom + 0.1, 2.0);
    this.uiState.updateWorkspace({ zoom: newZoom });
  }

  onZoomOut() {
    const currentZoom = this.uiState.getCurrentState().workspace.zoom;
    const newZoom = Math.max(currentZoom - 0.1, 0.5);
    this.uiState.updateWorkspace({ zoom: newZoom });
  }

  onResetZoom() {
    this.uiState.updateWorkspace({ zoom: 1.0 });
  }

  onExportJson() {
    this.builderService.exportConfiguration();
  }

  onImportJson() {
    // Dispara clique do input de arquivo
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.json';
    fileInput.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        this.builderService.importConfiguration(file);
      }
    };
    fileInput.click();
  }

  getCurrentZoom(): number {
    return Math.round(this.uiState.getCurrentState().workspace.zoom * 100);
  }

  isPreviewMode(): boolean {
    return this.uiState.getCurrentState().workspace.previewMode;
  }

  isGridVisible(): boolean {
    return this.uiState.getCurrentState().workspace.gridVisible;
  }

  // Novos Métodos de Funcionalidades Fase 3 & 4
  onToggleFloatingInspector() {
    const currentState = this.uiState.getCurrentState();
    const isCurrentlyFloating = currentState.layout.mode === 'floating' && currentState.inspector.visible;
    
    if (isCurrentlyFloating) {
      // Oculta inspetor flutuante
      this.uiState.updateInspector({ visible: false });
    } else {
      // Mostra inspetor flutuante
      this.uiState.updateLayout({ mode: 'floating' });
      this.uiState.updateInspector({ visible: true });
    }
  }

  isFloatingInspectorActive(): boolean {
    const currentState = this.uiState.getCurrentState();
    return currentState.layout.mode === 'floating' && currentState.inspector.visible;
  }

  onShowPresets() {
    // Emite evento para mostrar modal de presets
    this.showPresets.emit();
  }

  onShowAISuggestions() {
    // Emite evento para mostrar painel de sugestões de IA
    this.showAISuggestions.emit();
  }

  onShowHelp() {
    this.showHelp.emit();
  }

  onShowTemplates() {
    this.showTemplates.emit();
  }

  onOpenTemplateManager() {
    this.openTemplateManager.emit();
  }

  onOpenAutoScreenGenerator() {
    this.openAutoScreenGenerator.emit();
  }

  onOpenOptimizedAutoScreenGenerator() {
    this.openOptimizedAutoScreenGenerator.emit();
  }

  onOpenSaveTemplateModal() {
    this.openSaveTemplateModal.emit();
  }

  onOpenLauncherModal() {
    this.openLauncherModal.emit();
  }
}
